<template>
  <div class="gs-player-section">
    <gs-field label="Player theme" hint="Sets the shape of the control bar. Your colours are applied on top.">
      <div class="gs-radio-cards" role="radiogroup" aria-label="Player theme">
        <label
          v-for="theme in availableThemes"
          :key="theme.value"
          class="gs-radio-card"
          :class="{ 'is-selected': selectedTheme === theme.value }"
        >
          <input
            type="radio"
            class="gs-radio-card__input"
            :name="'player-theme-' + _uid"
            :value="theme.value"
            :checked="selectedTheme === theme.value"
            :data-theme="theme.value"
            @change="setTheme(theme.value)"
          />
          <img v-if="isModern" class="gv-theme-preview" :src="themePreview(theme.value)" alt="" />
          <span v-else class="gs-theme-sketch" :class="'gs-theme-sketch--' + theme.key" aria-hidden="true">
            <span class="gs-theme-sketch__progress"></span>
            <span class="gs-theme-sketch__bar">
              <span class="gs-theme-sketch__dot"></span>
              <span class="gs-theme-sketch__line"></span>
            </span>
          </span>
          <span class="gs-radio-card__title">{{ theme.text }}</span>
        </label>
      </div>
    </gs-field>

    <div class="gs-player-section__colours">
      <gs-field label="Primary colour" hint="Used for the progress bar and the active controls.">
        <color-picker
          data-cy="model.settings.color.primary"
          v-model="model.settings.color.primary"
          :colors="tailwindConfig.colors"
          :cookies-origin="'groovevideo_video_' + model.id"
          @change="changeColor()"
        ></color-picker>
      </gs-field>

      <gs-field label="Secondary colour" hint="Used for the icons and the text on the control bar.">
        <color-picker
          data-cy="model.settings.color.secondary"
          v-model="model.settings.color.secondary"
          :colors="tailwindConfig.colors"
          :cookies-origin="'groovevideo_video_' + model.id"
          @change="changeColor()"
        ></color-picker>
      </gs-field>
    </div>
  </div>
</template>

<script>
import { isModernVideo, THEMES, playImage, syncModernAppearance } from './newAppearance';
import ColorPicker from '@groovepages/gd-ui-kit/components/ColorPicker';
import tailwindConfig from '@groovepages/gp-page-ui-kit/tailwind';

export default {
  name: 'ThemeSection',

  components: { ColorPicker },

  props: {
    model: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      // Values are video.js theme classes and must not change.
      themes: [
        { key: 'default', text: 'Default', value: 'vjs-theme-default' },
        { key: 'youtube', text: 'YouTube', value: 'vjs-theme-youtube' },
        { key: 'vimeo', text: 'Vimeo', value: 'vjs-theme-vimeo' },
        { key: 'wistia', text: 'Wistia', value: 'vjs-theme-wistia' },
      ],
      tailwindConfig,
    };
  },

  computed: {
    isModern() { return isModernVideo(this.model); },
    selectedTheme() { return this.isModern ? this.model.settings.appearance.theme : (this.model.settings.theme || 'vjs-theme-default'); },
    availableThemes() { return this.isModern ? THEMES.map(theme => ({ key: theme.id, value: theme.id, text: theme.label })) : this.themes; },
  },
  methods: {
    themePreview(id) { return playImage(id, THEMES.find(theme => theme.id === id).primary); },
    setTheme(value) {
      if (this.isModern) {
        const theme = THEMES.find(item => item.id === value);
        this.model.settings.appearance.theme = value;
        if (value !== 'custom') this.model.settings.color.primary = theme.primary;
        syncModernAppearance(this.model);
      } else {
        this.model.settings.theme = value;
      }
      this.$emit('updateModel', this.model);
      this.changeTheme();
    },

    // Ported from step1.
    changeTheme() {
      this.$store.dispatch('videoTheme', this.model.settings.theme);
      this.$emit('forceRerender');
    },

    // step1 declared a `changeColor` that was never reachable — the pickers were
    // bound with `v-model` alone — and it dispatched `playerPrimaryColor` /
    // `playerSecondaryColor`. Both of those actions commit a mutation the store
    // does not define (`setPlayerPrimaryColor` against `setplayerPrimaryColor`),
    // so calling them only logs `[vuex] unknown mutation type`. The preview
    // reads the two colours straight off the model, so telling the shell is all
    // this has to do. Fixing the store is not this task's file.
    changeColor() {
      syncModernAppearance(this.model);
      this.$emit('updateModel', this.model);
      this.$emit('forceRerender');
    },
  },
};
</script>

<style scoped>
.gv-theme-preview { width: 100%; height: 76px; object-fit: contain; background: #202333; border-radius: 6px; }
.gs-player-section {
  display: flex;
  flex-direction: column;
  gap: 18px;
  padding-top: 14px;
}

.gs-radio-cards {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
}

.gs-radio-card {
  display: flex;
  position: relative;
  flex-direction: column;
  gap: 10px;
  padding: 16px;
  background: var(--gs-surface);
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-md);
  cursor: pointer;
}

.gs-radio-card.is-selected {
  background: var(--gs-active-soft);
  border-color: var(--gs-active-border);
}

.gs-radio-card__input {
  position: absolute;
  top: 14px;
  right: 14px;
  accent-color: var(--gs-active);
}

.gs-radio-card__title {
  color: var(--gs-foreground);
  font-size: 14px;
  font-weight: 650;
}

/* A three-line sketch of the control bar each theme draws. */
.gs-theme-sketch {
  display: flex;
  flex-direction: column;
  gap: 6px;
  justify-content: flex-end;
  height: 46px;
  padding: 8px;
  background: var(--gs-surface-subtle);
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-sm);
}

.gs-theme-sketch__progress {
  height: 3px;
  background: var(--gs-border-strong);
  border-radius: 999px;
}

.gs-theme-sketch__bar {
  display: flex;
  gap: 6px;
  align-items: center;
}

.gs-theme-sketch__dot {
  flex: none;
  width: 8px;
  height: 8px;
  background: var(--gs-border-strong);
  border-radius: 999px;
}

.gs-theme-sketch__line {
  flex: 1 1 auto;
  height: 3px;
  background: var(--gs-border);
  border-radius: 999px;
}

/* Each theme draws its own bar: YouTube squares it off, Vimeo rounds every
   control, Wistia sits taller. The sketch only has to be recognisable. */
.gs-theme-sketch--youtube .gs-theme-sketch__progress,
.gs-theme-sketch--youtube .gs-theme-sketch__line {
  border-radius: 0;
}

.gs-theme-sketch--vimeo .gs-theme-sketch__dot {
  width: 14px;
  border-radius: var(--gs-radius-sm);
}

.gs-theme-sketch--wistia .gs-theme-sketch__progress {
  height: 5px;
}

.gs-theme-sketch--wistia .gs-theme-sketch__bar {
  gap: 10px;
}

.gs-player-section__colours {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}

@media (max-width: 640px) {
  .gs-radio-cards { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .gs-player-section__colours {
    grid-template-columns: minmax(0, 1fr);
  }
}
</style>

<!--
  Unscoped on purpose. `.gs-refresh-controls` sizes every text input to 40px
  with a higher specificity than the color picker's own HEX field. That
  stretches the field out of the picker and the typed value never lands in
  the visible box. This selector matches that rule and then adds the picker
  class so only these two inputs opt out.
-->
<style>
.gs-refresh-controls .hu-color-picker input:not([type="checkbox"]):not([type="radio"]):not([type="color"]):not([type="range"]) {
  min-height: 0;
  height: 30px;
  padding: 0 12px;
  border: 0;
  border-radius: 0;
  box-shadow: none;
}
</style>
